import { __awaiter, __generator } from 'tslib';
import publisherThemeList from '@highereducation/edu-product-toolkit-publishers/dist/data/publisherThemeList';
import generateDomain from '../../utils/generateDomain.js';
import { loadPublisherConfiguration } from '../../utils/loadPublisherConfiguration.js';
import preloadedPublisherConfigs from '../../data/index.js';
import ConfigurationLoadError from '../ConfigurationLoadError/ConfigurationLoadError.js';
import { ValidationError, validateConfiguration } from '../../utils/validators/index.js';
import { createLogger } from '../../utils/logger.js';
import ConfigurationCache from '../ConfigurationCache/ConfigurationCache.js';
import { CACHE_CONSTANTS } from '../../constants/cache.js';

/**
 * PublisherManager is a singleton class that manages publisher configurations.
 * It dynamically loads configuration for publishers from individual JSON files as needed.
 * It provides methods to get the entire configuration or only the theme for a specific host,
 * as well as a method to get the default configuration or theme.
 *
 * // Example Usage:
 * import { publisherManager } from '@highereducation/publishers';
 *
 * // Get configurations
 * const defaultConfig = publisherManager.getDefaultPublisherConfig();
 * const config = await publisherManager.getPublisherConfigByHost('example.com');
 *
 * // Get themes
 * const defaultTheme = publisherManager.getDefaultPublisherTheme();
 * const theme = publisherManager.getPublisherThemeByHost(window.location.host);
 *
 */
var PublisherManager = /** @class */ (function () {
    // Private constructor to enforce singleton pattern
    function PublisherManager() {
        this.logger = createLogger({
            name: 'PublisherManager',
            logLevel: CACHE_CONSTANTS.DEFAULT_LOG_LEVEL,
        });
        // Validate default config before initializing cache
        var defaultConfig = validateConfiguration(preloadedPublisherConfigs['default']);
        this.cache = new ConfigurationCache(CACHE_CONSTANTS, defaultConfig, this.logger);
    }
    /**
     * Loads the publisher configuration for a given domain. It first attempts to retrieve
     * the configuration from a preloaded set of configurations. If not found, it fetches
     * the configuration asynchronously. The configuration is then validated and cached
     * for future use.
     *
     * @param {string} domain - The domain for which the publisher configuration is to be loaded.
     * @returns A promise that resolves to the validated publisher configuration.
     * @throws {ConfigurationLoadError} If the configuration fails to load or validate.
     *
     * @private
     */
    PublisherManager.prototype.loadPublisherConfigByHost = function (domain) {
        return __awaiter(this, void 0, void 0, function () {
            var config, _a, validatedConfig, error_1, isValidationError;
            var _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 4, , 5]);
                        if (!((_b = preloadedPublisherConfigs[domain]) !== null && _b !== void 0)) return [3 /*break*/, 1];
                        _a = _b;
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, loadPublisherConfiguration(domain)];
                    case 2:
                        _a = (_c.sent());
                        _c.label = 3;
                    case 3:
                        config = _a;
                        validatedConfig = validateConfiguration(config);
                        this.cache.set(domain, validatedConfig);
                        return [2 /*return*/, validatedConfig];
                    case 4:
                        error_1 = _c.sent();
                        isValidationError = error_1 instanceof ValidationError ||
                            (error_1 instanceof Error && error_1.name === 'ValidationError');
                        // Handle validation errors specifically
                        if (isValidationError) {
                            this.logger.error('Configuration validation failed', {
                                domain: domain,
                                error: error_1,
                            });
                            throw new ConfigurationLoadError("Configuration validation failed for ".concat(domain), 'CONFIG_VALIDATION_ERROR', { cause: error_1 });
                        }
                        // Handle all other errors
                        this.logger.error('Configuration load failed', {
                            domain: domain,
                            error: error_1 instanceof Error ? error_1 : 'Unknown error',
                        });
                        throw new ConfigurationLoadError("Failed to load configuration for ".concat(domain), 'CONFIG_LOAD_ERROR', { cause: error_1 instanceof Error ? error_1 : new Error('Unknown error') });
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Retrieves the singleton instance of the `PublisherManager` class.
     * If the instance does not already exist, it initializes a new one.
     *
     * @returns {PublisherManager} The singleton instance of `PublisherManager`.
     */
    PublisherManager.getInstance = function () {
        if (!PublisherManager.instance) {
            PublisherManager.instance = new PublisherManager();
        }
        return PublisherManager.instance;
    };
    /**
     * Retrieves the theme configuration for a publisher based on the provided host.
     *
     * @param {string} host - The host name for which the publisher theme is to be retrieved.
     * @returns A promise that resolves to the `Theme` object associated with the publisher.
     *
     * @remarks
     * This method generates a domain from the provided host and attempts to retrieve
     * the publisher configuration from the cache. If the configuration is not found
     * in the cache, it loads the configuration asynchronously.
     */
    PublisherManager.prototype.getPublisherThemeByHost = function (host) {
        return __awaiter(this, void 0, void 0, function () {
            var domain, config, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        domain = generateDomain(host, 'default');
                        _a = this.cache.get(domain);
                        if (_a) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.loadPublisherConfigByHost(domain)];
                    case 1:
                        _a = (_b.sent());
                        _b.label = 2;
                    case 2:
                        config = _a;
                        return [2 /*return*/, config.theme];
                }
            });
        });
    };
    /**
     * Retrieves the publisher configuration for a given host.
     *
     * @param {string} host - The host name for which the publisher configuration is requested.
     * @returns A promise that resolves to the `Publisher` object associated with the given host.
     *          If the configuration is cached, it is returned directly; otherwise, it is loaded asynchronously.
     */
    PublisherManager.prototype.getPublisherConfigByHost = function (host) {
        return __awaiter(this, void 0, void 0, function () {
            var domain, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        domain = generateDomain(host, 'default');
                        _a = this.cache.get(domain);
                        if (_a) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.loadPublisherConfigByHost(domain)];
                    case 1:
                        _a = (_b.sent());
                        _b.label = 2;
                    case 2: return [2 /*return*/, (_a)];
                }
            });
        });
    };
    /**
     * Retrieves the default publisher theme from the cache.
     *
     * @returns {Theme} The default theme associated with the publisher.
     */
    PublisherManager.prototype.getDefaultPublisherTheme = function () {
        // We can be confident this exists because it's initialized in constructor
        return this.cache.get('default').theme;
    };
    /**
     * Retrieves the default publisher configuration from the cache.
     *
     * @returns {Publisher} The default publisher configuration.
     */
    PublisherManager.prototype.getDefaultPublisherConfig = function () {
        // We can be confident this exists because it's initialized in constructor
        return this.cache.get('default');
    };
    PublisherManager.prototype.getPublisherThemeFromListByHost = function (host) {
        var domain = generateDomain(host, 'default');
        var publisherTheme = publisherThemeList.find(function (publisher) { return publisher.publisherName === domain; });
        return (publisherTheme === null || publisherTheme === void 0 ? void 0 : publisherTheme.theme) || null;
    };
    return PublisherManager;
}());
var publisherManager = PublisherManager.getInstance();

export { PublisherManager, publisherManager };
